import React from 'react'
import { T } from '@transifex/react'

import { createApprovalCollectionRouteMap } from './approval-collection'
import { createNotificationRouteMap } from './notifications'
import { createPickupHistoryRouteMap } from './pickup-history'
import { createRouteProblemErrorsRoutesMap } from './route-problem-errors'
import { createRouteSchemeRouteMap } from './route-schemes'
import { createRoutesRouteMap } from './routes'
import { createTruckloadsRouteMap } from './truckloads'
import { createWeightControlRouteMap } from './weight-control'
import { pathJoin } from '../../../utils'

export const createOperationManagementRouteMap = (prefix: string) => ({
  // Hauler office front door (PRO-66): the tabbed Overview (Scorecards …
  // Invoices · Settlement). The old per-view URLs below (settlement,
  // reliability, …) are kept as redirect sources — no dead bookmarks.
  overview: pathJoin(prefix)('overview').chainWithTitle({
    title: <T _str="Overview" />,
    breadCrumbTitle: <T _str="Overview" />,
  }),
  // Hauler office Activities (PRO-66/PRO-74): Tasks · Proposals · Complaints ·
  // Unloading as tabs of one destination.
  activities: pathJoin(prefix)('activities').chainWithTitle({
    title: <T _str="Activities" />,
    breadCrumbTitle: <T _str="Activities" />,
  }),
  routes: pathJoin(prefix)(`routes`).chainMapAuto(createRoutesRouteMap, {
    title: <T _str="All Routes" />,
    breadCrumbTitle: <T _str="All Routes" />,
  }),
  'routes/create': pathJoin(prefix)('routes')('create').chain,
  'route-schemes': pathJoin(prefix)('route-schemes').chainMapAuto(
    createRouteSchemeRouteMap,
    {
      title: <T _str="All Route Schemes" />,
      breadCrumbTitle: <T _str="All Route Schemes" />,
    }
  ),
  // The URL segment keeps its historical name (bookmarks); the page is the
  // pickup WORKLIST now, so every label says "Pickups" — breadcrumbs, the
  // omnisearch route directory and the tab strips all read this title.
  'pickup-history': pathJoin(prefix)('pickup-history').chainMapAuto(
    createPickupHistoryRouteMap,
    {
      title: <T _str="Pickups" />,
      breadCrumbTitle: <T _str="Pickups" />,
    }
  ),
  // HSY L1a (reqs 219/220): the load (kuorma) table. NOT called "Loads" —
  // the Overview already renders an office-visible "Loads" option meaning
  // per-contract-area performance, and the contractor Performance view a
  // "Load"; two screens with one name in a demo session is a needless own
  // goal.
  truckloads: pathJoin(prefix)('truckloads').chainMapAuto(
    createTruckloadsRouteMap,
    {
      title: <T _str="Truckloads" />,
      breadCrumbTitle: <T _str="Truckloads" />,
    }
  ),
  'route-problem-errors': pathJoin(prefix)(`route-problem-errors`).chainMapAuto(
    createRouteProblemErrorsRoutesMap,
    {
      title: <T _str="Route Problem Errors" />,
      breadCrumbTitle: <T _str="Route Problem Errors" />,
    }
  ),
  'weight-control': pathJoin(prefix)(`weight-control`).chainMapAuto(
    createWeightControlRouteMap,
    {
      title: <T _str="Weight Control" />,
      breadCrumbTitle: <T _str="Weight Control" />,
    }
  ),
  // Task 06 (REQ 3.5.58, PRODUCT-83 M4): the holiday-impact work queue.
  // Office-only strip tab (RouteStudioSectionNav/OperationsSectionNav),
  // gated on the `useHolidaySurface().impactRead` probe — plan F3.
  'holiday-impact': pathJoin(prefix)('holiday-impact').chainWithTitle({
    title: <T _str="Holiday impact" />,
    breadCrumbTitle: <T _str="Holiday impact" />,
  }),
  // HSY-182 (§6.7): wash-list planning and approval. Strip tab on both the
  // Route Studio and Operations strips, hidden — and the route redirected —
  // unless the tenant's `container_washing_enabled` flag is on
  // (src/hooks/useHsyFlags, read at render time).
  'wash-lists': pathJoin(prefix)('wash-lists').chainWithTitle({
    title: <T _str="Wash lists" />,
    breadCrumbTitle: <T _str="Wash lists" />,
  }),
  // REQ 3.5.60 (PRO-642): the per-vehicle driven-routes report, with the
  // REQ 3.5.61 (PRO-643) track-on-map slice. Live-Operations gated, same as
  // 'live-monitoring' — it reads the same module's data.
  'driven-routes': pathJoin(prefix)('driven-routes').chainWithTitle({
    title: <T _str="Driven routes" />,
    breadCrumbTitle: <T _str="Driven routes" />,
  }),
  'approval-collection': pathJoin(prefix)(`approval-collection`).chainMapAuto(
    createApprovalCollectionRouteMap,
    {
      title: <T _str="Approval collection" />,
      breadCrumbTitle: <T _str="Approval collection" />,
    }
  ),
  notification: pathJoin(prefix)('notification').chainMapAuto(
    createNotificationRouteMap,
    {
      title: <T _str="Notification" />,
      breadCrumbTitle: <T _str="Notification" />,
    }
  ),
  // URL segment stays 'live-monitoring' (bookmark safety, ADR-0002 #7); only
  // the human-facing title/breadcrumb is renamed to "Live Operations" (D5).
  'live-monitoring': pathJoin(prefix)('live-monitoring').chainWithTitle({
    title: <T _str="Live Operations" />,
    breadCrumbTitle: <T _str="Live Operations" />,
  }),
  proposals: pathJoin(prefix)('proposals').chainWithTitle({
    title: <T _str="Change proposals" />,
    breadCrumbTitle: <T _str="Change proposals" />,
  }),
  tasks: pathJoin(prefix)('tasks').chainWithTitle({
    title: <T _str="Tasks" />,
    breadCrumbTitle: <T _str="Tasks" />,
  }),
  unloading: pathJoin(prefix)('unloading').chainWithTitle({
    title: <T _str="Unloading report" />,
    breadCrumbTitle: <T _str="Unloading report" />,
  }),
  settlement: pathJoin(prefix)('settlement').chainWithTitle({
    title: <T _str="Settlement" />,
    breadCrumbTitle: <T _str="Settlement" />,
  }),
  reliability: pathJoin(prefix)('reliability').chainWithTitle({
    title: <T _str="Performance" />,
    breadCrumbTitle: <T _str="Performance" />,
  }),
  feedback: pathJoin(prefix)('feedback').chainWithTitle({
    title: <T _str="Complaints" />,
    breadCrumbTitle: <T _str="Complaints" />,
  }),
})
